import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Archive } from '@untitledui/icons/Archive';
import { ArrowLeft } from '@untitledui/icons/ArrowLeft';
import { ChevronDown } from '@untitledui/icons/ChevronDown';
import { DotsVertical } from '@untitledui/icons/DotsVertical';
import { Edit01 } from '@untitledui/icons/Edit01';
import { Plus } from '@untitledui/icons/Plus';
import { RefreshCcw01 } from '@untitledui/icons/RefreshCcw01';
import { Rows01 } from '@untitledui/icons/Rows01';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import type { Project } from '@/api/core-api/types.gen';

import { ProgramStatus, ProjectSortField } from '@/api/core-api/types.gen';
import { EmptyState } from '@/components/application/empty-state/empty-state';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { ConfirmationModal } from '@/components/base/modals/confirmation-modal';
import { Tabs } from '@/components/base/tabs/tabs';
import { ResearcherShell } from '@/components/navigation/researcher-shell';
import { ProgramFormModal } from '@/components/programs/program-form-modal';
import { ProjectListTable } from '@/components/projects/project-list-table';
import {
  useArchiveProgram,
  useUnarchiveProgram,
  useUpdateProgram,
} from '@/hooks/api/use-program-mutations';
import {
  programParticipantsQueryOptions,
  programQueryOptions,
} from '@/hooks/api/use-programs';
import {
  MAX_PAGE_SIZE,
  teamProjectsQueryOptions,
} from '@/hooks/api/use-team-projects';
import {
  type ProjectListActions,
  useProjectListActions,
} from '@/hooks/use-project-list-actions';
import { NewProjectSetupModal } from '@/routes/researchers/_authenticated/projects/-components/new-project-setup-modal';

import { AddProjectsModal } from './-components/add-projects-modal';
import { ProgramParticipantsTable } from './-components/program-participants-table';
import { resolveProgramProjects } from './-program-projects';

const TABS = ['projects', 'participants'] as const;

const programDetailSearchSchema = z.object({
  tab: z.enum(TABS).catch('projects').default('projects'),
});

export const Route = createFileRoute(
  '/researchers/_authenticated/programs/$programId',
)({
  validateSearch: programDetailSearchSchema,
  component: ProgramDetailPage,
});

function ProgramDetailPage() {
  const { user, activeDashboard } = Route.useRouteContext();
  const { programId } = Route.useParams();
  const teamId = activeDashboard.team ?? '';
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isAddProjectsOpen, setIsAddProjectsOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);

  const updateProgram = useUpdateProgram(teamId, user.id);
  const archiveProgram = useArchiveProgram(teamId, user.id);
  const unarchiveProgram = useUnarchiveProgram(teamId, user.id);

  // The row action menu (duplicate / preview / delete / …) reuses the projects
  // tab's actions wholesale. Its mutations invalidate the `team-projects` cache
  // this page's project query lives under, so a delete or copy reconciles here
  // without extra plumbing. `dialog` is the confirmation modal it drives.
  const projectActions = useProjectListActions(teamId, true);

  const {
    data: program,
    isPending,
    isError,
    refetch,
  } = useQuery({
    ...programQueryOptions(teamId, programId, user.id),
    enabled: !!teamId,
  });

  // Membership lives on the project now, so a program's projects are fetched by
  // filtering the projects endpoint to this program — the response is exactly
  // its projects, so there is nothing to reconcile and no dangling case.
  const programIds = useMemo(() => [programId], [programId]);

  const { data: projectsData, isPending: isProjectsPending } = useQuery({
    ...teamProjectsQueryOptions({
      teamId,
      authUserId: user.id,
      page: 1,
      pageSize: MAX_PAGE_SIZE,
      programIds,
      sortBy: ProjectSortField.CREATED_AT,
      sortDir: 'desc',
    }),
    enabled: !!teamId,
  });

  const memberProjects = projectsData?.results ?? [];

  // The roster is the expensive read of the feature — it merges two collections
  // per request. It is fetched here rather than inside the participants tab
  // because the header counts come from the same payload, so gating it on the
  // tab would either duplicate the work or leave the header blank.
  //
  // Skipped for a program with no projects: the roster derives from them, so
  // with none there is provably no one to find, and the page renders its empty
  // state instead of the stats and tabs this would feed.
  const {
    data: participantsData,
    isPending: isParticipantsPending,
    isError: isParticipantsError,
  } = useQuery({
    ...programParticipantsQueryOptions(teamId, programId, user.id),
    enabled: !!teamId && memberProjects.length > 0,
  });

  // Wait for both the program and its projects before deciding empty-vs-not, or
  // the full-page empty state would flash before the projects arrive.
  if (isPending || isProjectsPending) {
    return (
      <ResearcherShell user={user} activeDashboard={activeDashboard}>
        <div className="px-6 py-8">
          <div className="mx-auto max-w-360">
            <div className="h-40 animate-pulse rounded-xl bg-primary ring-1 ring-secondary" />
          </div>
        </div>
      </ResearcherShell>
    );
  }

  if (isError || !program) {
    return (
      <ResearcherShell user={user} activeDashboard={activeDashboard}>
        <div className="px-6 py-8">
          <div className="mx-auto max-w-360">
            <div className="rounded-xl border border-secondary bg-primary p-12 text-center">
              <p className="text-sm text-tertiary">
                <Trans>Could not load this program.</Trans>
              </p>
              <Button
                color="link-color"
                size="sm"
                className="mt-2"
                onPress={() => refetch()}
              >
                <Trans>Try again</Trans>
              </Button>
            </div>
          </div>
        </div>
      </ResearcherShell>
    );
  }

  const { projects, projectCount, breakdown } = resolveProgramProjects(
    programId,
    memberProjects,
  );
  const isArchived = program.status === ProgramStatus.ARCHIVED;

  /**
   * A program with no projects has nothing to summarise and nothing to tab
   * between — the stats would all read zero and the participants tab could not
   * have anyone in it. So the whole body below the header becomes one
   * full-height empty state pointing at the only useful next action.
   *
   * `min-h-full` rather than `h-full`: it fills the viewport when the content is
   * short (the point of this branch) but still grows past it for a long project
   * list, which a fixed height would clip.
   */
  const hasNoProjects = projectCount === 0;

  return (
    <ResearcherShell user={user} activeDashboard={activeDashboard}>
      <div className="flex min-h-full flex-col px-6 py-8">
        <div className="mx-auto flex w-full max-w-360 flex-1 flex-col">
          <Link
            to="/researchers/programs"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-tertiary hover:text-secondary"
          >
            <ArrowLeft className="size-4" />
            <Trans>All programs</Trans>
          </Link>

          {/* Header */}
          <div className="mt-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-display-xs font-semibold text-primary">
                  {program.name}
                </h1>
                {isArchived && (
                  <Badge color="gray" size="md" type="pill-color">
                    <Trans>Archived</Trans>
                  </Badge>
                )}
              </div>
              {program.description && (
                <p className="mt-2 max-w-3xl text-sm text-tertiary">
                  {program.description}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {/* One primary action. "Create new" and "Add existing" are two ways
                  into the same intent, so they collapse into a single split of
                  one button rather than competing for attention. Suppressed while
                  the program is empty — the full-page empty state owns those two
                  choices there. */}
              {!isArchived && !hasNoProjects && (
                <Dropdown.Root>
                  <Button
                    size="xs"
                    color="primary"
                    iconLeading={Plus}
                    iconTrailing={ChevronDown}
                  >
                    {t`Add study`}
                  </Button>
                  <Dropdown.Popover>
                    <Dropdown.Menu
                      onAction={(key) => {
                        if (key === 'create') setIsNewProjectOpen(true);
                        if (key === 'existing') setIsAddProjectsOpen(true);
                      }}
                    >
                      <Dropdown.Item id="create" label={t`Create new study`} />
                      <Dropdown.Item
                        id="existing"
                        label={t`Add existing study`}
                      />
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown.Root>
              )}

              {/* Secondary, infrequent actions (Edit, Archive) collapse into an
                  overflow to the right of the primary action. An icon-only xs
                  Button rather than `Dropdown.DotsButton` so it matches the
                  primary button's height instead of sitting a touch taller. */}
              <Dropdown.Root>
                <Button
                  size="xs"
                  color="secondary"
                  iconLeading={DotsVertical}
                  aria-label={t`More actions`}
                />
                <Dropdown.Popover>
                  <Dropdown.Menu
                    onAction={(key) => {
                      if (key === 'edit') setIsRenameOpen(true);
                      if (key === 'archive') setIsArchiveOpen(true);
                      if (key === 'unarchive')
                        unarchiveProgram.mutate(programId);
                    }}
                  >
                    <Dropdown.Item id="edit" icon={Edit01} label={t`Edit`} />
                    {isArchived ? (
                      <Dropdown.Item
                        id="unarchive"
                        icon={RefreshCcw01}
                        label={t`Unarchive`}
                      />
                    ) : (
                      <Dropdown.Item
                        id="archive"
                        icon={Archive}
                        label={t`Archive`}
                      />
                    )}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.Root>
            </div>
          </div>

          {hasNoProjects ? (
            <EmptyProgram
              isArchived={isArchived}
              onAddExisting={() => setIsAddProjectsOpen(true)}
              onCreateNew={() => setIsNewProjectOpen(true)}
            />
          ) : (
            <>
              {/* Derived figures. Stored nowhere — recomputed on every read. */}
              <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <SummaryStat
                  label={t`Studies`}
                  value={String(projectCount)}
                  detail={
                    breakdown.length > 0
                      ? breakdown
                          .map(
                            (entry) =>
                              `${entry.count} ${entry.label.toLowerCase()}`,
                          )
                          .join(' · ')
                      : undefined
                  }
                />
                <SummaryStat
                  label={t`Unique participants`}
                  value={
                    isParticipantsPending
                      ? '—'
                      : String(participantsData?.uniqueParticipants ?? 0)
                  }
                  detail={t`across all studies`}
                />
                <SummaryStat
                  label={t`Sessions completed`}
                  value={
                    isParticipantsPending
                      ? '—'
                      : String(participantsData?.sessionsCompleted ?? 0)
                  }
                  detail={t`Attended or paid`}
                />
              </dl>

              {/* Tabs */}
              <Tabs
                className="mt-8"
                selectedKey={search.tab}
                onSelectionChange={(key) =>
                  void navigate({
                    search: { tab: key === 'participants' ? key : 'projects' },
                    replace: true,
                  })
                }
              >
                <Tabs.List
                  type="underline"
                  items={[
                    {
                      id: 'projects',
                      label: t`Studies`,
                      badge:
                        projectCount > 0 ? String(projectCount) : undefined,
                    },
                    {
                      id: 'participants',
                      label: t`Participants`,
                      // No badge until the roster resolves — a "0" that later
                      // becomes "47" reads as a real answer while it loads.
                      badge: participantsData
                        ? String(participantsData.uniqueParticipants)
                        : undefined,
                    },
                  ]}
                >
                  {(item) => (
                    <Tabs.Item id={item.id} badge={item.badge}>
                      {item.label}
                    </Tabs.Item>
                  )}
                </Tabs.List>

                <Tabs.Panel id="projects" className="pt-6">
                  <ProgramProjectsList
                    projects={projects}
                    actions={projectActions.callbacks}
                  />
                </Tabs.Panel>

                <Tabs.Panel id="participants" className="pt-6">
                  <ProgramParticipantsTable
                    data={participantsData}
                    isPending={isParticipantsPending}
                    isError={isParticipantsError}
                    teamId={teamId}
                    authUserId={user.id}
                    programId={programId}
                    projects={projects}
                  />
                </Tabs.Panel>
              </Tabs>
            </>
          )}
        </div>
      </div>

      <ProgramFormModal
        isOpen={isRenameOpen}
        onOpenChange={(open) => {
          setIsRenameOpen(open);
          if (!open) updateProgram.reset();
        }}
        initialValues={{
          name: program.name,
          description: program.description,
        }}
        isSaving={updateProgram.isPending}
        errorMessage={
          updateProgram.isError ? t`Could not save the program.` : undefined
        }
        onSubmit={(values) =>
          updateProgram.mutate(
            { programId, ...values },
            { onSuccess: () => setIsRenameOpen(false) },
          )
        }
      />

      <AddProjectsModal
        isOpen={isAddProjectsOpen}
        onOpenChange={setIsAddProjectsOpen}
        teamId={teamId}
        authUserId={user.id}
        programId={programId}
      />

      {/* The ordinary project-creation funnel, entered with this program
          attached. Reused rather than reimplemented: creation is a long flow
          with its own platform and study-type decisions, and a program is not a
          reason to fork it. The `programId` rides through the funnel and is
          applied once the project exists — see `ProjectCreateNavState`. */}
      <NewProjectSetupModal
        isOpen={isNewProjectOpen}
        onOpenChange={setIsNewProjectOpen}
        organizationId={activeDashboard.organization}
        programId={programId}
        useStudyTerminology
      />

      {/* Confirmation modals for the row action menu (duplicate/close/delete/
          archive). Rendered once here, driven by `useProjectListActions`. */}
      {projectActions.dialog}

      <ConfirmationModal
        isOpen={isArchiveOpen}
        onOpenChange={setIsArchiveOpen}
        title={t`Archive this program?`}
        description={t`It stays available and nothing is deleted — it just drops out of the default list. You can still open it from “Show archived”.`}
        confirmLabel={t`Archive program`}
        isLoading={archiveProgram.isPending}
        onCancel={() => setIsArchiveOpen(false)}
        onConfirm={() =>
          archiveProgram.mutate(programId, {
            onSuccess: () => setIsArchiveOpen(false),
          })
        }
      />
    </ResearcherShell>
  );
}

function SummaryStat({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <dt className="text-sm font-medium text-tertiary">{label}</dt>
      <dd className="mt-1 text-display-xs font-semibold text-primary">
        {value}
      </dd>
      {detail && <p className="mt-1 text-xs text-tertiary">{detail}</p>}
    </div>
  );
}

/**
 * The whole body of a program that has no projects yet.
 *
 * Fills the remaining height rather than sitting as a short card under three
 * zeroed-out stats: with no projects there is no summary to read and no second
 * tab worth visiting, so the page should ask for the one thing it needs instead
 * of rendering a skeleton of a populated program.
 *
 * An archived program keeps the explanation but loses the call to action —
 * consistent with the header, which also hides its write actions when archived.
 */
function EmptyProgram({
  isArchived,
  onAddExisting,
  onCreateNew,
}: {
  isArchived: boolean;
  onAddExisting: () => void;
  onCreateNew: () => void;
}) {
  return (
    <div className="mt-6 flex min-h-96 flex-1 items-center justify-center rounded-xl bg-primary px-6 py-16 ring-1 ring-secondary">
      <EmptyState size="sm">
        <EmptyState.Header pattern="dot" patternSize="lg">
          <Rows01 className="relative z-10 size-6 text-fg-secondary" />
        </EmptyState.Header>
        <EmptyState.Content>
          <EmptyState.Title className="text-lg">
            <Trans>No studies in this program yet</Trans>
          </EmptyState.Title>
          <EmptyState.Description>
            {isArchived ? (
              <Trans>
                This program is archived, so studies can no longer be added to
                it.
              </Trans>
            ) : (
              <Trans>
                Start a new study in this program, or bring in one you have
                already run. A study can only be in one program, and cannot be
                removed once added.
              </Trans>
            )}
          </EmptyState.Description>
        </EmptyState.Content>
        {/* Two ways in, because a program is worth creating both before the
            research starts and after some of it already happened. "Create new"
            leads, since a brand-new program most often precedes its projects. */}
        {!isArchived && (
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button
              color="primary"
              size="md"
              iconLeading={Plus}
              onClick={onCreateNew}
            >
              {t`Create new study`}
            </Button>
            <Button color="secondary" size="md" onClick={onAddExisting}>
              {t`Add existing study`}
            </Button>
          </div>
        )}
      </EmptyState>
    </div>
  );
}

/**
 * The program's projects. Only rendered when there is at least one — the empty
 * case is {@link EmptyProgram}, which replaces the entire page body rather than
 * just this panel.
 *
 * Renders the same {@link ProjectListTable} the projects tab uses, so type,
 * created date, status and the row action menu behave identically here — rather
 * than a second, drifting layout. Alerts stay empty: the program view does not
 * fetch unread/payment counts, and the shared table renders that column blank
 * rather than making this caller invent them.
 *
 * No "since deleted" footer: membership is a `programId` on the project, so a
 * deleted project simply is not returned and the count is whatever came back.
 * A duplicate is a separate case — it is a fresh draft that does not inherit the
 * program unless the researcher opts in, so it appears in the projects tab.
 */
function ProgramProjectsList({
  projects,
  actions,
}: {
  projects: Project[];
  actions: ProjectListActions;
}) {
  return (
    <ProjectListTable
      size="sm"
      projects={projects}
      useStudyTerminology
      onPause={actions.onPause}
      onUnpause={actions.onUnpause}
      onClose={actions.onClose}
      onDelete={actions.onDelete}
      onArchive={actions.onArchive}
      onCopy={actions.onCopy}
    />
  );
}
